import { useRouteError, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, LinearProgress, Stack, Typography } from '@mui/material'

import HapanaLogo from '@hapana/shared/web/styles/assets/images/hapana-logo-green-full.png'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_SIZE,
  BUTTON_THEME,
  BUTTON_VARIANTS,
} from '@hapana/shared/web/components/shared/button/button.constants'

import {
  trackEnhancedError,
  toTrackableError,
  ERROR_SCREEN_FEATURE,
  ErrorScreen,
} from '../../services/enhanced-error-tracking'

import { styles } from './root-error-boundary.styles'

// A deploy replaces the hashed chunk files, so a tab opened before it can no longer fetch the
// chunk it asks for next. That is a routine consequence of shipping, not a fault the member or
// staff member caused — it gets the calm "we just updated" screen rather than the error one.
const CHUNK_LOAD_ERROR_SIGNATURES = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'ChunkLoadError',
  'Unable to preload CSS',
  'error loading dynamically imported module',
]

const AUTO_RELOAD_SECONDS = 3

const RootErrorBoundary = () => {
  const error = useRouteError()
  const navigate = useNavigate()
  const { t } = useTranslation('common')

  const isChunkLoadError =
    CHUNK_LOAD_ERROR_SIGNATURES.some((signature) => error?.message?.includes(signature)) ||
    error?.name === 'ChunkLoadError'

  const [secondsRemaining, setSecondsRemaining] = useState(AUTO_RELOAD_SECONDS)

  useEffect(() => {
    trackEnhancedError(toTrackableError(error), {
      feature: ERROR_SCREEN_FEATURE,
      componentName: 'RootErrorBoundary',
      metadata: {
        error_screen: ErrorScreen.Root,
        is_chunk_load_error: isChunkLoadError,
      },
    })
  }, [error, isChunkLoadError])

  // Counts the reload down in the UI rather than reloading out from under the reader, so the
  // screen explains itself even on the slow connections where it lingers.
  useEffect(() => {
    if (!isChunkLoadError) {
      return undefined
    }

    const tick = setInterval(() => {
      setSecondsRemaining((remaining) => Math.max(0, remaining - 1))
    }, 1000)
    const reload = setTimeout(() => {
      window.location.reload()
    }, AUTO_RELOAD_SECONDS * 1000)

    return () => {
      clearInterval(tick)
      clearTimeout(reload)
    }
  }, [isChunkLoadError])

  const handleReload = () => {
    window.location.reload()
  }

  const handleGoHome = () => {
    navigate('/')
  }

  return (
    <Box sx={styles.root}>
      <Box
        component="img"
        src={HapanaLogo}
        alt={t('common:errorBoundary.logoAlt')}
        sx={styles.logo}
      />

      <Stack sx={styles.card}>
        <Typography typography="header2" component="h1" sx={styles.title}>
          {isChunkLoadError
            ? t('common:errorBoundary.updateTitle')
            : t('common:errorBoundary.errorTitle')}
        </Typography>

        <Typography typography="body" sx={styles.description}>
          {isChunkLoadError
            ? t('common:errorBoundary.updateDescription')
            : t('common:errorBoundary.errorDescription')}
        </Typography>

        {isChunkLoadError && (
          <>
            <LinearProgress sx={styles.progress} />
            <Typography typography="small" sx={styles.countdown}>
              {secondsRemaining > 0
                ? t('common:errorBoundary.refreshingInSeconds', { count: secondsRemaining })
                : t('common:errorBoundary.refreshingNow')}
            </Typography>
          </>
        )}

        {process.env.NODE_ENV === 'development' && error && (
          <Box sx={styles.errorDetails}>
            <Typography variant="caption" component="pre" sx={styles.errorDetailsText}>
              {error.message || JSON.stringify(error, null, 2)}
            </Typography>
          </Box>
        )}

        <Box sx={styles.actions}>
          <Button
            onClick={handleReload}
            variant={BUTTON_VARIANTS.CONTAINED}
            theme={BUTTON_THEME.PRIMARY}
            size={BUTTON_SIZE.MEDIUM}
            data-testid="root-error-boundary-refresh"
            label={
              isChunkLoadError
                ? t('common:errorBoundary.refreshNow')
                : t('common:errorBoundary.tryAgain')
            }
          />
          {!isChunkLoadError && (
            <Button
              onClick={handleGoHome}
              variant={BUTTON_VARIANTS.OUTLINED}
              theme={BUTTON_THEME.GRAY}
              size={BUTTON_SIZE.MEDIUM}
              data-testid="root-error-boundary-go-home"
              label={t('common:errorBoundary.goHome')}
            />
          )}
        </Box>

        {!isChunkLoadError && (
          <Typography typography="small" sx={styles.supportText}>
            {t('common:errorBoundary.support')}{' '}
            <Typography
              component="a"
              typography="small"
              href="mailto:support@hapana.com"
              sx={styles.supportLink}
            >
              support@hapana.com
            </Typography>
          </Typography>
        )}
      </Stack>

      <Box sx={styles.backgroundPatternImage} />
    </Box>
  )
}

export default RootErrorBoundary
